import { useQuery } from '@tanstack/react-query';

import { useEffect, useMemo, useState } from 'react';

import { Delete, Restore, Visibility } from '@mui/icons-material';
import { Chip, Dialog, DialogContent, DialogTitle, Link as MuiLink, Stack } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { UserInterfaceClient } from '@@api';
import { formatTimestampFull, useApiClient, useIsMobile, useToastService } from '@@common';
import { ConfirmDialog, DialogHeaderActions, getResponsiveDataGridProps } from '@@components';

import { BundleDefinitionSummaryModel } from '../../../api/api';
import { useChangeSet } from '../../../components/ChangeSetContext';
import RestoreAppVersionDialog from './RestoreAppVersionDialog';

const pageSizeOptions = [10, 20, 50];
const initialPaginationModel: GridPaginationModel = {
	pageSize: pageSizeOptions[1],
	page: 0,
};

type AppVersionsDialogProps = {
	bundle: BundleDefinitionSummaryModel | null;
	changeSetNameById: Map<string, string>;
	onClose: () => void;
	onDeleted: () => void;
};

export default function AppVersionsDialog({ bundle, changeSetNameById, onClose, onDeleted }: AppVersionsDialogProps) {
	const client = useApiClient(UserInterfaceClient);
	const toastService = useToastService();
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const { currentChangeSetId } = useChangeSet();
	const restoreChangeSetId = currentChangeSetId || bundle?.changeSetId;
	const [restoreVersion, setRestoreVersion] = useState<BundleDefinitionSummaryModel | null>(null);

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(initialPaginationModel);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState<BundleDefinitionSummaryModel | null>(null);
	const [isMaximized, setIsMaximized] = useState(false);

	useEffect(() => {
		setPaginationModel(initialPaginationModel);
		setIsMaximized(false);
	}, [bundle?.definitionId]);

	const handleClose = () => {
		setIsMaximized(false);
		onClose();
	};

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['userinterface', 'bundles', 'versions', bundle?.definitionId, paginationModel],
		queryFn: () => client.searchVersions(bundle!.definitionId!, paginationModel.page, paginationModel.pageSize),
		enabled: Boolean(bundle?.definitionId),
	});

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize + 1 : data.items.length) : 0),
		[paginationModel, data],
	);

	const handleDeleteVersion = (version: BundleDefinitionSummaryModel) => {
		setSelectedVersion(version);
		setOpenDeleteDialog(true);
	};

	const handleConfirmDeleteVersion = async () => {
		if (!selectedVersion) return;
		if (!selectedVersion.definitionId) {
			toastService.error('Cannot delete bundle version without a definition ID.');
			return;
		}

		try {
			await client.deleteVersion(selectedVersion.definitionId, selectedVersion.id);
			toastService.success(`Bundle version ${selectedVersion.versionId} deleted successfully.`);
			onDeleted();
			refetch();
		} catch (error) {
			console.error('Error deleting bundle version:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete bundle version: ${errorMessage}`);
		} finally {
			setOpenDeleteDialog(false);
			setSelectedVersion(null);
		}
	};

	const handleCancelDeleteVersion = () => {
		setOpenDeleteDialog(false);
		setSelectedVersion(null);
	};

	const columns: GridColDef<BundleDefinitionSummaryModel>[] = [
		{
			field: 'versionId',
			headerName: 'Version',
			width: 100,
			sortable: false,
			renderCell: (params) => bundle?.definitionId && params.row.id ? (
				<MuiLink component={RouterLink} to={`/app/${bundle.definitionId}/version/${params.row.id}`} underline="hover">
					{params.value}
				</MuiLink>
			) : params.value,
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			sortable: false,
		},
		{
			field: 'tags',
			headerName: 'Tags',
			minWidth: 220,
			flex: 1,
			sortable: false,
			renderCell: ({ row }) => (
				<Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto', alignItems: 'center', height: '100%' }}>
					{row.tags?.map((tag) => {
						const restoreSource = /^restored-from:v(\d+):(.+)$/.exec(tag);
						return <Chip key={tag} title={tag} label={restoreSource ? `Restored from v${restoreSource[1]}` : tag} size="small" variant="outlined" />;
					})}
				</Stack>
			),
		},
		{
			field: 'changeSetId',
			headerName: 'Change Set',
			flex: 1,
			sortable: false,
			valueGetter: (value: string) => changeSetNameById.get(value) ?? value,
		},
		{
			field: 'updatedByName',
			headerName: 'Modified By',
			flex: 1,
			sortable: false,
			valueGetter: (_value, row) => row.updatedByName ?? row.updatedById ?? row.createdByName ?? row.createdById ?? '',
		},
		{
			field: 'updatedDate',
			headerName: 'Modified',
			flex: 1,
			sortable: false,
			valueFormatter: (value: string) => (value ? formatTimestampFull(value) : ''),
		},
		{
			field: 'createdDate',
			headerName: 'Created',
			flex: 1,
			sortable: false,
			valueFormatter: (value: string) => (value ? formatTimestampFull(value) : ''),
		},
		{
			field: 'previousBundleDefinitionId',
			headerName: 'Previous ID',
			flex: 1,
			sortable: false,
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: 'Actions',
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem
					key="view-snapshot"
					icon={<Visibility />}
					label="View snapshot"
					disabled={!bundle?.definitionId || !params.row.id}
					onClick={() => navigate(`/app/${bundle!.definitionId}/version/${params.row.id}`)}
					showInMenu
				/>,
				<GridActionsCellItem key="delete" icon={<Delete />} label="Delete" onClick={() => handleDeleteVersion(params.row)} showInMenu />,
				<GridActionsCellItem
					key="restore"
					icon={<Restore />}
					label="Restore as latest"
					disabled={!params.row.definitionId || !restoreChangeSetId}
					onClick={() => setRestoreVersion(params.row)}
					showInMenu
				/>,
			],
		},
	];

	return (
		<>
			<Dialog open={Boolean(bundle)} onClose={handleClose} maxWidth="lg" fullWidth fullScreen={isMaximized}>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
					<span style={{ flex: 1 }}>{bundle ? `${bundle.name} Versions` : 'Bundle Versions'}</span>
					<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={handleClose} />
				</DialogTitle>
				<DialogContent sx={{ height: { xs: 'calc(100vh - 112px)', sm: 520 }, minHeight: 320 }}>
					<DataGrid
						loading={isLoading}
						rows={data?.items ?? []}
						rowCount={rowCount}
						columns={columns}
						paginationMode="server"
						sortingMode="client"
						disableColumnFilter
						pageSizeOptions={pageSizeOptions}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						columnVisibilityModel={{
							changeSetId: !isMobile,
							updatedByName: !isMobile,
							updatedDate: !isMobile,
							createdDate: !isMobile,
							previousBundleDefinitionId: !isMobile,
						}}
						{...getResponsiveDataGridProps({ isMobile })}
					/>
				</DialogContent>
			</Dialog>
			<RestoreAppVersionDialog
				version={restoreVersion}
				changeSetId={restoreChangeSetId}
				changeSetName={restoreChangeSetId ? changeSetNameById.get(restoreChangeSetId) : undefined}
				onClose={() => setRestoreVersion(null)}
			/>
			<ConfirmDialog
				open={openDeleteDialog}
				title="Delete Bundle Version"
				message={
					selectedVersion
						? `Are you sure you want to delete version ${selectedVersion.versionId} of "${selectedVersion.name}"? Current latest versions cannot be deleted.`
						: ''
				}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDeleteVersion}
				onCancel={handleCancelDeleteVersion}
			/>
		</>
	);
}
